import { computed, Directive, effect, ElementRef, HostBinding, inject, input, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, fromEvent, map, switchMap, EMPTY } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { RichTextEditorComponent } from 'shared/feature-rich-text-editor';
import { RichTextViewComponent } from 'shared/feature-rich-text-view';
import { PopoverService } from 'shared/ui-common';
import { isTruthy } from 'shared/util-common';

import { AnchoredComment, AnchoredTextComment, CommentType } from '../comment.model';
import { CommentService } from '../comment.service';
import {
  addMutationObserverToContainer,
  getRangeOffsets,
  getUpdatedComment,
  markCommentInContainer,
  unmarkCommentInContainer,
} from '../comment.service.util';
import { AddCommentPopoverComponent } from '../components/add-comment-popover/add-comment-popover.component';

type NewCommentSelection = {
  start: number;
  length: number;
  position: DOMRect;
};

@Directive({
  selector: 'common-rich-text-editor[ttCommentableText],common-rich-text-view[ttCommentableText]',
  standalone: true,
})
export class CommentableTextDirective implements OnDestroy {
  private commentsService = inject(CommentService, { optional: true });
  private element = inject(ElementRef);
  private popoverService = inject(PopoverService);
  private richTextEditorComponent = inject(RichTextEditorComponent, { optional: true });
  private richTextViewComponent = inject(RichTextViewComponent, { optional: true });

  public widgetId = input.required<string>();
  public elementId = input.required<string>();

  private commentableContainerId = computed(() => `tt-commentable-${this.elementId()}`);

  private observer: MutationObserver | undefined;
  private clickListener: ((event: Event) => void) | undefined;
  private isMarkingComments = false;

  private comments = computed(() => {
    if (!this.commentsService) return [];

    const all = this.commentsService.comments() ?? [];
    return all
      .filter(
        (comment) =>
          comment.anchor.widgetId === this.widgetId() &&
          comment.anchor.elementId === this.elementId() &&
          !this.commentsService?.isOriginalContentUnavailable(comment),
      )
      .map((comment) => comment as AnchoredTextComment);
  });

  private selection$ = this.commentsService
    ? fromEvent(this.element.nativeElement, 'mouseup').pipe(
        filter(() => this.commentsService!.initialized() && !this.commentsService!.readonly()),
        debounceTime(300),
        map((): NewCommentSelection | undefined => {
          const selection = document.getSelection();
          if (!selection || selection.isCollapsed) return;

          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          if (rect.width === 0 && rect.height === 0) return;

          const { start, length } = getRangeOffsets(this.element.nativeElement, range);
          if (length <= 0) return;

          return { start, length, position: rect };
        }),
        takeUntilDestroyed(),
      )
    : EMPTY;

  @HostBinding('class')
  get hostClasses(): string {
    return this.commentsService ? this.commentableContainerId() : '';
  }

  constructor() {
    if (!this.commentsService) {
      return; // 🚀 fully inactive
    }

    this.selection$
      .pipe(
        switchMap((selection) => {
          if (!selection) return EMPTY;

          const allowedTypes = this.commentsService!.getNewCommentTypes();
          if (allowedTypes.length === 0) return EMPTY;

          return this.popoverService
            .open(AddCommentPopoverComponent, selection.position, {
              data: { allowedTypes },
              positionStrategy: 'top-center',
              scrollStrategy: 'scroll',
              hasBackdrop: false,
            })
            .afterClosed()
            .pipe(
              filter((type): type is CommentType => type !== undefined && type !== null),
              map((type) => ({ selection, type })),
            );
        }),
      )
      .subscribe(({ selection: { start, length }, type }) => {
        this.addComment(start, length, this.element.nativeElement.textContent?.substring(start, start + length) || '', type).catch(
          (error) => console.error('Error adding comment:', error),
        );
      });

    effect(() => {
      const comments = this.comments();
      if (comments.length > 0) {
        this.addInitialComments();
      }
    });

    this.clickListener = (event: Event) => {
      if (!this.commentsService) return;

      const target = event.target as HTMLElement;
      const match = target.closest<HTMLElement>('[class*="tt-comments-comment-"]');
      if (!match) return;

      const commentClass = Array.from(match.classList).find(
        (className) => className.startsWith('tt-comments-comment-') && className !== 'tt-comments-comment--selected',
      );
      if (!commentClass) return;

      this.commentsService.selectCommentByCommentId(commentClass.slice('tt-comments-comment-'.length));
      event.stopPropagation();
    };

    this.element.nativeElement.addEventListener('click', this.clickListener);

    (this.richTextEditorComponent ?? this.richTextViewComponent)?.initialized.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.comments().length > 0) {
        this.addInitialComments();
      }
    });
  }

  private async addInitialComments() {
    if (!this.commentsService) return;

    const comments = this.comments();
    this.isMarkingComments = true;

    try {
      await Promise.all(
        comments.map(async (comment) => {
          const markElement = this.element.nativeElement.querySelector(`.tt-comments-comment-${comment.id}`);
          if (markElement) return;

          await markCommentInContainer(
            comment,
            this.commentsService!.selectedComment()?.comment?.id === comment.id,
            this.element.nativeElement,
          );
        }),
      );
    } finally {
      this.isMarkingComments = false;
      this.addObserver();
    }
  }

  private async addComment(start: number, length: number, selectedContent: string, type: CommentType) {
    if (!this.commentsService) return;

    const newComment: AnchoredComment = {
      id: crypto.randomUUID(),
      text: '',
      type,
      anchor: {
        type: 'text',
        start,
        length,
        elementId: this.elementId(),
        widgetId: this.widgetId(),
        selectedContent,
      },
    };

    this.isMarkingComments = true;

    try {
      await markCommentInContainer(newComment, false, this.element.nativeElement);
      this.commentsService.addComment(newComment);
    } finally {
      this.isMarkingComments = false;
    }

    document.getSelection()?.removeAllRanges();
  }

  private removeComment(commentId: string) {
    if (!this.commentsService) return;

    const comment = this.comments().find((item) => item.id === commentId);
    if (comment && this.commentsService.shouldPreserveAnchor(comment)) {
      void this.addInitialComments();
      return;
    }

    this.commentsService.deleteComment(commentId);
    unmarkCommentInContainer(commentId, this.element.nativeElement);
  }

  private addObserver() {
    if (!this.commentsService) return;

    this.removeObserver();

    this.observer = addMutationObserverToContainer(this.element.nativeElement, () => {
      if (this.isMarkingComments) return;

      const markElements = Array.from(this.element.nativeElement.querySelectorAll('mark') as NodeListOf<HTMLElement>);

      const comments = markElements
        .filter((mark) => mark.innerText.replace(/\u200B/g, '').trim().length > 0)
        .map((mark) => mark.className.match(/tt-comments-comment-(\S+)/)?.[1])
        .filter(isTruthy)
        .map((id) => this.comments().find((comment) => comment.id === id))
        .filter(isTruthy);

      const serviceComments = this.comments();

      if (comments.length === 0 && serviceComments.length > 0) {
        setTimeout(() => {
          if (this.isMarkingComments) return;

          const recheckMarks = this.element.nativeElement.querySelectorAll('mark[class*="tt-comments-comment-"]');

          if (recheckMarks.length === 0) {
            serviceComments.forEach((comment) => this.removeComment(comment.id));
          }
        }, 200);
        return;
      }

      comments.forEach((comment) => {
        if (!comment || this.commentsService?.shouldPreserveAnchor(comment)) return;
        const updated = getUpdatedComment(comment, this.element.nativeElement);

        if (
          updated &&
          (updated.anchor.start !== comment.anchor.start ||
            updated.anchor.length !== comment.anchor.length ||
            updated.anchor.selectedContent !== comment.anchor.selectedContent)
        ) {
          this.commentsService!.editComment(updated);
        }
      });

      serviceComments.forEach((comment) => {
        if (!comments.find((c) => c?.id === comment.id)) {
          this.removeComment(comment.id);
        }
      });
    });
  }

  private removeObserver() {
    this.observer?.disconnect();
    this.observer = undefined;
  }

  public ngOnDestroy() {
    this.removeObserver();

    if (this.clickListener) {
      this.element.nativeElement.removeEventListener('click', this.clickListener);
      this.clickListener = undefined;
    }
  }
}
